<template>
  <StandardModal
    standard-modal-name="CompleteListingModal"
    :title="$t('complete_listing_title')"
    :no-shadow="true"
    :esc-close="true"
    content-height="max-content"
    @close="$emit('close')"
  >
    <div slot="content" class="complete-listing-content">
      <CompleteListingCard
        :proposition="proposition"
        :title="$t('complete_listing_modal_card_title')"
        :highlight-first-step="true"
        :preloaded-summary="firstResidenceSummary"
        :step-keys="stepKeys"
        @cta-click="$emit('close')"
      />

      <div v-if="recentVisitorsCount > 0" class="visitors-footer">
        <div v-if="visitorAvatars.length" class="visitor-avatars">
          <span
            v-for="(avatar, idx) in visitorAvatars"
            :key="idx"
            class="visitor-avatar"
            :style="{ backgroundImage: `url('${avatar}')` }"
            aria-hidden="true"
          />
        </div>
        <p class="visitors-text">
          <span class="visitors-line-main">
            <strong>{{ recentVisitorsCount }}</strong>
            {{ $tc('complete_listing_visitors_suffix', recentVisitorsCount) }}
          </span>
          <span class="visitors-line-period">
            {{ $t('complete_listing_visitors_period') }}
          </span>
        </p>
      </div>
    </div>
  </StandardModal>
</template>

<script>
import StandardModal from '@/components/Modals/Standard.vue';
import { getVisitorAvatars } from '@/utils/recentVisitors';
import { modalStepKeys } from '@/utils/listingChecklist';
import CompleteListingCard from './CompleteListingCard.vue';

export default {
  name: 'CompleteListingModal',

  components: { StandardModal, CompleteListingCard },

  props: {
    proposition: {
      type: Object,
      required: true
    },
    // Fetched by completeListingModalTrigger before the modal opens.
    summary: {
      type: Object,
      required: true
    },
    recentVisitors: {
      type: Object,
      required: true
    }
  },

  computed: {
    // The steps count the first residence only; the gauge keeps the
    // backend's overall percentage.
    firstResidenceSummary() {
      return {
        ...this.summary,
        residences: (this.summary.residences || []).slice(0, 1)
      };
    },
    stepKeys() {
      return modalStepKeys(this.summary);
    },
    recentVisitorsCount() {
      return this.recentVisitors.count;
    },
    visitorAvatars() {
      return getVisitorAvatars(this.recentVisitors, 3);
    }
  }
};
</script>

<style lang="scss" scoped>
// StandardModal is 700px wide on desktop; this content reads better narrower.
.standard-modal-wrapper ::v-deep .standard-modal {
  @media ($desktop) {
    left: calc(50% - 280px);
    width: 560px;
  }
}

.complete-listing-content {
  display: flex;
  flex-direction: column;
  padding: 0 24px 24px;
}

.visitors-footer {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 24px 0 0;
  padding: 18px 0 0;
  border-top: 1px solid #f0f0f2;
}

.visitor-avatars {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.visitor-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.6px solid #ffffff;
  background-size: cover;
  background-position: center;
  background-color: #f3f4f6; // placeholder while image loads
  margin-left: -12px;

  &:first-child {
    margin-left: 0;
  }
}

.visitors-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  color: var(--Text-Primary);
}

.visitors-line-main strong {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.visitors-line-period {
  font-size: 12px;
  color: #6b7280;
}
</style>
